// src/pages/students/exams/StudentExamRegistrationNew.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useStudents } from '../../hooks/useStudents';
import { useSubjects } from '../../hooks/useSubjects';
import { useExamSessions } from '../../hooks/useExamSessions';
import { supabase } from '../../supabaseClient';

const REG_STATUS = ['Registered', 'Deferred', 'Cancelled'];

export default function StudentExamRegistrationNew() {
  const navigate = useNavigate();
  const { studentId: routeStudentId } = useParams();
  const studentId = String(routeStudentId || '');

  // Reference data
  const { students = [], loading: studentsLoading } = useStudents?.() ?? { students: [], loading: false };
  const { subjects = [], isLoading: subjectsLoading } = useSubjects?.() ?? { subjects: [], isLoading: false };
  const { examSessions = [], loading: sessionsLoading } = useExamSessions?.() ?? { examSessions: [], loading: false };

  // Form state
  const [sessionId, setSessionId] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState('Registered');
  const [venue, setVenue] = useState('');
  const [totalFeePaid, setTotalFeePaid] = useState(''); // keep as string for typing UX
  const [paymentDate, setPaymentDate] = useState('');
  const [remarks, setRemarks] = useState('');

  // Subjects selection
  const [selected, setSelected] = useState(() => new Set());

  // UX state
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Already-registered (in this session) -> disabled checkboxes
  const [alreadyInSession, setAlreadyInSession] = useState(() => new Set());
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Student name (read-only display)
  const studentName = useMemo(() => {
    const s = (students || []).find((x) => String(x.id) === studentId);
    return s?.full_name || s?.name || '—';
  }, [students, studentId]);

  const onToggleSubject = (sid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const canSubmit = useMemo(() => {
    return !!studentId && !!sessionId && selected.size > 0 && !saving;
  }, [studentId, sessionId, selected, saving]);

  const prettyError = (e) => {
    const msg = e?.message || String(e);
    if (/duplicate key value/i.test(msg)) return 'Duplicate registration detected. Some selected subjects may already be registered in this session.';
    if (/permission denied/i.test(msg)) return 'You do not have permission to perform this action.';
    return msg;
  };

  // Compute next attempt_no per subject for this student
  const fetchAttemptNumbers = useCallback(async (stuId, subjectIds) => {
    if (!subjectIds.length) return {};
    const { data, error } = await supabase
      .from('student_exam_info')
      .select('subject_id, attempt_no')
      .eq('student_id', stuId)
      .eq('is_deleted', false)
      .in('subject_id', subjectIds);
    if (error) throw error;

    const maxBySubject = {};
    for (const row of data || []) {
      const key = String(row.subject_id);
      const current = maxBySubject[key] ?? 0;
      maxBySubject[key] = Math.max(current, Number(row.attempt_no || 0));
    }
    const next = {};
    for (const sid of subjectIds) {
      const key = String(sid);
      next[key] = (maxBySubject[key] ?? 0) + 1;
    }
    return next;
  }, []);

  // Upsert parent for (student, session) and set parent fields
  const findOrCreateParent = useCallback(
    async (stuId, sessId, fields) => {
      const { data: found, error: fErr } = await supabase
        .from('student_exam_session')
        .select('id')
        .eq('student_id', stuId)
        .eq('exam_session_id', sessId)
        .eq('is_deleted', false)
        .limit(1);
      if (fErr) throw fErr;

      const parentPayload = {
        student_id: stuId,
        exam_session_id: sessId,
        registration_status: fields.registration_status || 'Registered',
        venue: fields.venue || null,
        total_fee_paid:
          fields.total_fee_paid === '' || fields.total_fee_paid == null
            ? null
            : Number(fields.total_fee_paid),
        payment_date: fields.payment_date || null,
        remarks: fields.remarks || null,
        is_deleted: false,
      };

      if (found && found.length > 0) {
        const parentId = found[0].id;
        const { error: uErr } = await supabase
          .from('student_exam_session')
          .update(parentPayload)
          .eq('id', parentId);
        if (uErr) throw uErr;
        return parentId;
      }

      const { data: created, error: cErr } = await supabase
        .from('student_exam_session')
        .insert(parentPayload)
        .select('id')
        .single();
      if (cErr) throw cErr;
      return created.id;
    },
    []
  );

  // When student or session changes, check existing registration and prefill parent fields
  const loadExistingInSession = useCallback(async (stuId, sessId) => {
    setAlreadyInSession(new Set());
    if (!stuId || !sessId) return;
    setLoadingExisting(true);
    try {
      const { data: parentRows, error: pErr } = await supabase
        .from('student_exam_session')
        .select('id, registration_status, venue, total_fee_paid, payment_date, remarks')
        .eq('student_id', stuId)
        .eq('exam_session_id', sessId)
        .eq('is_deleted', false)
        .limit(1);
      if (pErr) throw pErr;

      if (parentRows && parentRows.length > 0) {
        const parentId = parentRows[0].id;
        const p = parentRows[0];

        setRegistrationStatus(p.registration_status || 'Registered');
        setVenue(p.venue || '');
        setTotalFeePaid(p.total_fee_paid == null ? '' : String(p.total_fee_paid));
        setPaymentDate(p.payment_date || '');
        setRemarks(p.remarks || '');

        // load already-registered child subjects for this parent
        const { data: children, error: cErr } = await supabase
          .from('student_exam_info')
          .select('subject_id')
          .eq('session_registration_id', parentId)
          .eq('is_deleted', false);
        if (cErr) throw cErr;

        const setIds = new Set((children || []).map((r) => String(r.subject_id)));
        setAlreadyInSession(setIds);

        // unselect anything already registered
        setSelected((prev) => {
          const next = new Set(prev);
          for (const sid of Array.from(next)) {
            if (setIds.has(String(sid))) next.delete(sid);
          }
          return next;
        });
      } else {
        // fresh parent defaults
        setRegistrationStatus('Registered');
        setVenue('');
        setTotalFeePaid('');
        setPaymentDate('');
        setRemarks('');
        setAlreadyInSession(new Set());
      }
    } catch (e) {
      console.error(e);
      setFormError(e?.message || 'Failed to load existing registrations for this session.');
    } finally {
      setLoadingExisting(false);
    }
  }, []);

  useEffect(() => {
    setFormError('');
    setSelected(new Set());
    loadExistingInSession(studentId, sessionId);
  }, [studentId, sessionId, loadExistingInSession]);

  const handleCancel = () => navigate(`/students/${studentId}?tab=exams`);

  const handleSubmit = async () => {
    setFormError('');
    if (!canSubmit) return;

    setSaving(true);
    try {
      // drop subjects already registered in this session
      const chosen = Array.from(selected).map(String);
      const filtered = chosen.filter((sid) => !alreadyInSession.has(String(sid)));
      if (filtered.length === 0) {
        throw new Error('All selected subjects are already registered in this session.');
      }

      // 1) Upsert parent with form fields
      const parentId = await findOrCreateParent(studentId, sessionId, {
        registration_status: registrationStatus,
        venue,
        total_fee_paid: totalFeePaid,
        payment_date: paymentDate,
        remarks,
      });

      // 2) Compute attempt_no per subject (single query)
      const attemptsMap = await fetchAttemptNumbers(studentId, filtered);

      // 3) Build child rows
      const childRows = filtered.map((sid) => ({
        session_registration_id: parentId,
        student_id: studentId,
        subject_id: sid,
        attempt_no: attemptsMap[String(sid)] ?? 1,
        registration_status: 'Registered',
        result_status: 'Pending',
        is_deleted: false,
      }));

      // 4) Bulk insert children
      const { error: insErr } = await supabase.from('student_exam_info').insert(childRows);
      if (insErr) throw insErr;

      alert('Exam registration created successfully.');
      navigate(`/students/${studentId}?tab=exams`);
    } catch (e) {
      console.error(e);
      setFormError(prettyError(e));
    } finally {
      setSaving(false);
    }
  };

  // Derived lists (same sorting/labels as Admin)
  const sessionsOptions = useMemo(
    () => (examSessions || []).map((s) => ({ id: s.id, label: s.name })),
    [examSessions]
  );

  const subjectRows = useMemo(() => {
    return (subjects || []).map((subj) => ({
      id: subj.id,
      code: subj.code || subj.subject_code || '',
      name: subj.name || subj.subject_name || '',
    }));
  }, [subjects]);

  const disabledCount = alreadyInSession.size;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleCancel}>Back</Button>
          <h1 className="text-xl font-bold text-gray-900">New Exam Registration</h1>
        </div>

        <Card>
          <div className="mb-4 text-sm text-gray-600">
            Student is preselected. Choose an exam session, fill session-level details, then select one or more subjects to register.
            Subjects already registered for this session will be disabled.
          </div>

          {formError && (
            <div className="mb-4 p-3 rounded bg-red-100 text-red-700">{formError}</div>
          )}

          {/* Student (read-only) & Session (required) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Student */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student *</label>
              <div className="px-3 py-2 bg-gray-50 rounded border border-gray-200">
                {studentsLoading ? 'Loading…' : studentName}
              </div>
            </div>

            {/* Exam Session */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exam Session *</label>
              <select
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={sessionsLoading || saving}
              >
                <option value="">Select a session</option>
                {sessionsOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Parent Details (session-level) */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Registration Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registration Status</label>
              <select
                value={registrationStatus}
                onChange={(e) => setRegistrationStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={saving}
              >
                {REG_STATUS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Venue */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
              <Input
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                disabled={saving}
                placeholder="e.g., KL, SABAH"
              />
            </div>

            {/* Total Fee Paid */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fee Paid (optional)</label>
              <Input
                type="number"
                step="0.01"
                value={totalFeePaid}
                onChange={(e) => setTotalFeePaid(e.target.value)}
                disabled={saving}
                placeholder="e.g., 0.00"
              />
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date (optional)</label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Remarks */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                disabled={saving}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Optional remarks…"
              />
            </div>
          </div>

          {/* Subjects */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Subjects</h2>
              <div className="text-xs text-gray-500">
                {loadingExisting ? 'Checking existing…' : disabledCount > 0 ? `${disabledCount} already registered in this session` : null}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {subjectsLoading ? (
                <div className="col-span-full text-gray-600">Loading subjects…</div>
              ) : (subjects || []).length === 0 ? (
                <div className="col-span-full text-gray-600">No subjects found.</div>
              ) : (
                (subjects || []).map((subj) => {
                  const sid = String(subj.id);
                  const disabled = alreadyInSession.has(sid) || !studentId || !sessionId || saving;
                  const checked = selected.has(sid);
                  const code = subj.code || subj.subject_code || '';
                  const name = subj.name || subj.subject_name || '';
                  return (
                    <label key={sid} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${disabled ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => onToggleSubject(sid)}
                      />
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{code || name}</div>
                        {code && name && <div className="text-gray-600">{name}</div>}
                        {alreadyInSession.has(sid) && (
                          <div className="text-xs text-gray-500">Already registered in this session</div>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {saving ? 'Saving…' : 'Create Registration'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
